import Geohash from 'latlon-geohash';
import { Bounds } from './types';

import AppleMap from '@zoocasa/node-kit/map/apple/map';
import { SingleAddress } from '@zoocasa/go-search';

const base32 = '0123456789bcdefghjkmnpqrstuvwxyz'; // (geohash-specific) Base32 map

const MIN_LNG = -180;
const MAX_LNG = 180;
const WRAP_LNG_OFFSET = 360;

const MIN_LAT = -90;
const MAX_LAT = 90;

export type Precision = 1 | 2 |3 | 4 | 5 | 6 | 7 | 8;

/**
 * Parses a WKT point string into longitude and latitude coordinates
 * @param pointWkt WKT point string in format "POINT(longitude latitude)"
 * @returns Object containing longitude and latitude
 */
export function parseWktPoint(pointWkt: string): { longitude: number; latitude: number } {
  // Extract coordinates from WKT format "POINT(longitude latitude)"
  const match = pointWkt.match(/POINT\(([-\d.]+) ([-\d.]+)\)/);
  if (!match) {
    return { longitude: 0, latitude: 0 };
  }
  return {
    longitude: parseFloat(match[1]),
    latitude: parseFloat(match[2]),
  };
}

export function parseMultipolygonWktToCoordinates(wkt: string): { longitude: number; latitude: number }[][] {
  try {
    // Remove MULTIPOLYGON wrapper
    const cleanedWkt = wkt.replace(/MULTIPOLYGON\s*\(/, '').replace(/\)$/, '');
    
    // Split by polygon boundaries
    const polygons = cleanedWkt.split(/\),\s*\(/);
    
    return polygons.map(polygon => {
      // Remove leading and trailing parentheses
      const coords = polygon.replace(/^\(/, '').replace(/\)$/, '');
      
      // Split by comma and parse each coordinate pair
      const coordPairs = coords.split(',').map(coord => coord.trim());
      
      return coordPairs.map(pair => {
        const parts = pair.trim().split(/\s+/);
        
        if (parts.length !== 2) {
          return null;
        }
        
        const longitude = parseFloat(parts[0]);
        const latitude = parseFloat(parts[1]);
        
        if (isNaN(longitude) || isNaN(latitude)) {
          return null;
        }
        
        return { longitude, latitude };
      }).filter(coord => coord !== null);
    });
  } catch (error) {
    console.error('Error parsing WKT:', error);
    return [];
  }
}

// Returns the northwest corner of the map's current view
export function getNorthWest(bounds: Bounds) {
  return { lat: bounds.north, lng: bounds.west };
}

// Returns the southeast corner of the map's current view
export function getSouthEast(bounds: Bounds) {
  return { lat: bounds.south, lng: bounds.east };
}

/**
 * Calculates and returns all geohash strings that cover the geographical area defined by the provided
 * bounds at a specified precision level. If the precision level is set to 1, the function will return
 * all possible geohash strings for that level. For higher precision levels, it calculates the geohashes
 * for the corners of the bounding region and iteratively fills the area between these corners with
 * geohashes, ensuring full coverage.
 *
 * @param bounds The geographical bounds of the area for which to calculate geohashes.
 * @param precision The desired precision level for the geohashes. Precision levels
 *  range from 1 (covering broad areas) to 12 (covering very small areas).
 * @returns An array of unique geohash strings that cover the specified area at the desired precision level.
 *
 * @see https://www.movable-type.co.uk/scripts/geohash.html
 */
export function getGeohashesForBoundingRegion(bounds: Bounds, precision: number): string[] {
  const geohashes = new Set<string>();

  if (precision === 1) {
    for (let index = 0; index < base32.length; index++) {
      const geohash = base32.at(index);
      if (geohash) {
        geohashes.add(geohash);
      }
    }
    return Array.from(geohashes);
  }

  // Calculate corner geohashes
  const topLeftGeohash = Geohash.encode(bounds.north, bounds.west, precision);
  const topRightGeohash = Geohash.encode(bounds.north, bounds.east, precision);
  const bottomRightGeohash = Geohash.encode(bounds.south, bounds.east, precision);
  const bottomLeftGeohash = Geohash.encode(bounds.south, bounds.west, precision);

  // Add initial corner geohashes
  geohashes.add(topLeftGeohash);
  geohashes.add(topRightGeohash);
  geohashes.add(bottomRightGeohash);
  geohashes.add(bottomLeftGeohash);

  let rowStartGeohash = topLeftGeohash;
  let rowEndGeohash = topRightGeohash;
  let currentGeohash = rowStartGeohash;

  let continueRow = true;

  while (continueRow) {
    const eastNeighbour = Geohash.neighbours(currentGeohash).e;

    if (currentGeohash === bottomRightGeohash ) {
      continueRow = false;
    } else {
      if (currentGeohash === rowEndGeohash) {
        rowStartGeohash = Geohash.neighbours(rowStartGeohash).s;
        rowEndGeohash = Geohash.neighbours(rowEndGeohash).s;
        currentGeohash = rowStartGeohash;
        geohashes.add(rowStartGeohash);
        geohashes.add(rowEndGeohash);
      } else {
        geohashes.add(currentGeohash);
        currentGeohash = eastNeighbour;
      }
    }
  }
  return Array.from(geohashes);
}

/**
 * Returns the bounding region that corresponds to the specified geohash.
 */
export function getGeohashBoundingRegion(geohashString: string): Bounds {
  const bounds = Geohash.bounds(geohashString);
  return {
    north: bounds.ne.lat,
    south: bounds.sw.lat,
    east: bounds.ne.lon,
    west: bounds.sw.lon,
  };
}

/**
 * Adjusts a longitude value to ensure it falls within the valid range of -180 to 180 degrees.
 *
 * If the given longitude is outside this range, the function wraps it around the globe to
 * produce a valid longitude.
 *
 * @param lng The longitude value to be adjusted.
 * @returns The adjusted longitude value within the range of -180 to 180 degrees.
 *
 * @example
 * console.log(wrapLongitude(181)); // Output: -179
 * console.log(wrapLongitude(-181)); // Output: 179
 */
export function wrapLongitude(lng: number): number {
  if (lng < MIN_LNG) {
    lng = lng + WRAP_LNG_OFFSET;
  } else if (lng > MAX_LNG) {
    lng = lng - WRAP_LNG_OFFSET;
  }
  return lng;
}

/**
 * Clamps a latitude value to ensure it is within the valid range of -90 to 90 degrees.
 * If the latitude exceeds this range, it is set to the nearest valid value within the range.
 *
 * @param {number} lat - The latitude value to clamp.
 * @returns {number} The clamped latitude value, guaranteed to be within the range of -90 to 90 degrees.
 *
 * @example
 * console.log(clampLatitude(91)); // Output: 90
 * console.log(clampLatitude(-91)); // Output: -90
 * console.log(clampLatitude(45)); // Output: 45 (unchanged, as it's within the valid range)
 */
export function clampLatitude(lat: number): number {
  return Math.max(MIN_LAT, Math.min(MAX_LAT, lat));
}

/**
 * Returns the bounding region that corresponds to the area currently displayed by the map.
 */
export function getAppleMapBoundingRegion(map: AppleMap): Bounds {
  const boundingRegion = map.map.region.toBoundingRegion();

  return {
    north: boundingRegion.northLatitude,
    south: boundingRegion.southLatitude,
    east: boundingRegion.eastLongitude,
    west: boundingRegion.westLongitude,
  };
}

/**
 * Calculates the geographical center point of a given boundary.
 *
 * @param bounds The boundary defined by its north, south, east, and west coordinates.
 * @returns An object representing the latitude and longitude of the center point.
 */
export function calculateCenterPoint(bounds: Bounds): { lat: number; lng: number } {
  const lat = (bounds.north + bounds.south) / 2;
  const lng = (bounds.east + bounds.west) / 2;
  return { lat, lng };
}

export function getGeohashPrecisionFromZoom(zoom: number): Precision {
  if (zoom <= 3) return 1;
  if (zoom <= 6) return 2;
  if (zoom <= 8) return 3;
  if (zoom <= 11) return 4;
  if (zoom <= 13) return 5;
  if (zoom <= 15) return 6;
  if (zoom <= 17) return 7;
  return 8;
}

export function getGeohashPrecisionFromZoomMobile(zoom: number): Precision {
  if (zoom <= 3) return 1;
  if (zoom <= 4) return 2;
  if (zoom <= 5) return 2;
  if (zoom <= 6) return 2;
  if (zoom <= 7) return 3;
  if (zoom <= 8) return 3;
  if (zoom <= 9) return 4;
  if (zoom <= 10) return 4;
  if (zoom <= 11) return 5;
  if (zoom <= 12) return 5;
  if (zoom <= 13) return 5;
  if (zoom <= 14) return 6;
  if (zoom <= 15) return 7;
  return 8;
}

export function getNextZoomWithPrecisionChange(zoom: number) {
  if (zoom <= 3) return 4;
  if (zoom <= 6) return 7;
  if (zoom <= 8) return 9;
  if (zoom <= 11) return 12;
  if (zoom <= 13) return 13;
  if (zoom <= 15) return 16;
  if (zoom <= 17) return 18;
  return zoom++;
}

export function getNextZoomWithPrecisionChangeMobile(zoom: number) {
  if (zoom <= 3) return 4;
  if (zoom <= 4) return 7;
  if (zoom <= 5) return 7;
  if (zoom <= 6) return 7;
  if (zoom <= 7) return 9;
  if (zoom <= 8) return 9;
  if (zoom <= 9) return 11;
  if (zoom <= 10) return 11;
  if (zoom <= 11) return 14;
  if (zoom <= 12) return 14;
  if (zoom <= 13) return 14;
  if (zoom <= 14) return 15;
  if (zoom <= 15) return 16;
  return zoom++;
}

export function getOptimalZoomLevelForHybridPage(electedAddress: SingleAddress) {
  if (electedAddress.neighbourhood) {
    return 12;
  }
  if (electedAddress.subDivision) {
    return 11;
  }
  return 5;
}
